import { BrowserRouter, Routes, Route } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { queryClient } from '@/lib/queryClient';
import { Toaster } from '@/components/ui/sonner';
import { LoadingPage, ErrorFallback } from '@/components/common';
import { AppLayout } from '@/components/layout';
import {
  Auth0ProviderWithRoutes,
  AuthGuard,
  OnboardingGuard,
  RequireNoChild,
  AdminGuard,
  AuthProvider,
} from '@/components/auth';
import { NetworkProvider } from '@/components/network';
import { ServerStatusProvider } from '@/components/server-status';
import { SentryErrorBoundary } from '@/lib/sentry';

// Lazy load pages for code splitting
const TaskPreviewPage = lazy(() => import('@/pages/TaskPreviewPage'));
const TaskReportPage = lazy(() => import('@/pages/TaskReportPage'));
const LearnPage = lazy(() => import('@/pages/LearnPage'));
const PublicReportPage = lazy(() => import('@/pages/PublicReportPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const TasksPage = lazy(() => import('@/pages/TasksPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
const SignInPage = lazy(() => import('@/pages/SignInPage'));
const SignUpPage = lazy(() => import('@/pages/SignUpPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const ScanPage = lazy(() => import('@/pages/ScanPage'));
const TopicsPage = lazy(() => import('@/pages/TopicsPage'));
const TopicConfirmPage = lazy(() => import('@/pages/TopicsPage/TopicConfirmPage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const PrivacyPage = lazy(() => import('@/pages/LegalPage/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/LegalPage/TermsPage'));

function App() {
  return (
    <SentryErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <NetworkProvider>
          <BrowserRouter>
            <ServerStatusProvider>
              <Auth0ProviderWithRoutes>
                <AuthProvider>
                  <Suspense fallback={<LoadingPage message="Loading..." />}>
                  <Routes>
                    {/* ============================================ */}
                    {/* Public Routes */}
                    {/* ============================================ */}

                    {/* Auth pages */}
                    <Route path="/sign-in/*" element={<SignInPage />} />
                    <Route path="/sign-up/*" element={<SignUpPage />} />

                    {/* Legal pages - public, no auth required */}
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />

                    {/* Public learning page - share link entry */}
                    <Route path="/open/learn/:publicId" element={<LearnPage />} />
                    {/* Public report page - after completion */}
                    <Route path="/open/learn/:publicId/report" element={<PublicReportPage />} />

                    {/* ============================================ */}
                    {/* Protected Routes */}
                    {/* ============================================ */}

                    {/* Onboarding - requires login but no child profile */}
                    <Route
                      path="/onboarding"
                      element={
                        <AuthGuard>
                          <RequireNoChild>
                            <OnboardingPage />
                          </RequireNoChild>
                        </AuthGuard>
                      }
                    />

                    {/* Main layout with Sidebar */}
                    <Route
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <AppLayout />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    >
                      {/* Dashboard Home - Action-First Design */}
                      <Route path="/" element={<HomePage />} />
                      {/* Chat - Custom AI Requests */}
                      <Route path="/chat" element={<ChatPage />} />
                      <Route path="/c/:conversationId" element={<ChatPage />} />
                      {/* Quick Generate */}
                      <Route path="/scan" element={<ScanPage />} />
                      <Route path="/topics/:subject" element={<TopicsPage />} />
                      <Route path="/topics/:subject/confirm" element={<TopicConfirmPage />} />
                      {/* Tasks */}
                      <Route path="/tasks" element={<TasksPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      {/* Task Preview & Report - Parent view, same layout as other pages */}
                      <Route path="/task/:id" element={<TaskPreviewPage />} />
                      <Route path="/task/:id/report" element={<TaskReportPage />} />
                    </Route>

                    {/* Admin Dashboard (requires admin role) */}
                    <Route
                      path="/admin"
                      element={
                        <AuthGuard>
                          <AdminGuard>
                            <AdminPage />
                          </AdminGuard>
                        </AuthGuard>
                      }
                    >
                      <Route index element={null} />
                      <Route path=":tab" element={null} />
                    </Route>
                  </Routes>
                </Suspense>
                </AuthProvider>
              </Auth0ProviderWithRoutes>
            </ServerStatusProvider>
          </BrowserRouter>
          <Toaster />
        </NetworkProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
}

export default App;
